import React, { useState, useEffect } from "react";
import { api } from "../../api/apiConfig";
import { initialMasterData } from "./masterConfig";
import FormInput from "./FormInput";
import { useNavigate } from "react-router-dom";

const BoxItemMaster = () => {
  const { header, fields: initialFields } = initialMasterData;

  const [fields, setFields] = useState(initialFields);
  const [tableRows, setTableRows] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/box_master");

        if (Array.isArray(data)) {
          setTableRows(data);
        } else if (data && Array.isArray(data.data)) {
          setTableRows(data.data);
        } else {
          setTableRows([]);
        }
      } catch (err) {
        console.error("Error fetching box_master:", err);
        setTableRows([]);
      }
    })();
  }, []);


  const handleFieldChange = (index, value) => {
    const updated = [...fields];
    updated[index].value = value;
    setFields(updated);
  };

  useEffect(() => {
    const boxCode = fields.find(f => f.id === "box_code")?.value;
    const boxNameEng = fields.find(f => f.id === "box_name_eng")?.value;
    const hasValues = boxCode?.trim() && boxNameEng?.trim();

    setIsSaveDisabled(!hasValues);
    setIsEditEnabled(selectedRow !== null && hasValues);
    setIsDeleteEnabled(selectedRow !== null);
  }, [fields, selectedRow]);

  const handleSave = async () => {
    const newRow = Object.fromEntries(fields.map(f => [f.id, f.value]));
    try {
      const { data } = await api.post("/box_master", newRow);
      setTableRows((prev) => [...prev, data]);
      setFields(fields.map(f => ({ ...f, value: "" })));
    } catch (err) {
      console.error("Error saving:", err);
    }
  };


  const handleEdit = async () => {
    if (!selectedRow) return;

    const updatedRow = Object.fromEntries(fields.map(f => [f.id, f.value]));

    try {
      const { data } = await api.put(`/box_master/${selectedRow.id}`, updatedRow);
      setTableRows((prev) =>
        prev.map(row => (row.id === selectedRow.id ? data : row))
      );
      resetForm();
    } catch (err) {
      console.error("Error editing:", err);
    }
  };


  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await api.delete(`/box_master/${selectedRow.id}`);
      setTableRows((prev) => prev.filter(row => row.id !== selectedRow.id));
      resetForm();
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  const resetForm = () => {
    setFields(fields.map(f => ({ ...f, value: "" })));
    setSelectedRow(null);
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    const updatedFields = fields.map(f => ({
      ...f,
      value: row[f.id] || "",
    }));
    setFields(updatedFields);
  };

  return (
    <div className="h-screen w-screen bg-[#efeae0] flex items-center justify-center sm:p-4 overflow-hidden">
      <div className="w-full h-full border-[4px] border-black p-1 flex flex-col">
        <div className="border-[2px] border-black bg-[#eadacd] flex flex-col h-full">
          <div className="flex flex-col md:flex-row gap-2 p-2 flex-shrink-0">
            <div className="flex-1 flex flex-col justify-center bg-[#cfefe0] border-[2px] border-black p-3 text-center">
              <div className="font-bold text-base md:text-lg">{header.title}</div>
              <div className="text-xs md:text-sm">{header.subtitle}</div>
              <div className="text-xs md:text-sm">Phone : {header.phone}</div>
            </div>
            <div className="hidden lg:flex flex-1 items-center justify-center bg-[#cfefe0] border-[2px] border-black p-3">
              <div className="text-center">
                <div className="font-extrabold text-lg sm:text-xl md:text-2xl leading-none">BOX ITEM MASTER</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-2 p-2 flex-1 overflow-hidden">
            <div className="lg:w-1/2 border-[2px] border-black bg-[#cfefe0] p-3 flex flex-col gap-3">
              {fields.map((fld, idx) => (
                <FormInput key={fld.id} {...fld} onChange={(val) => handleFieldChange(idx, val)} />
              ))}
            </div>

            <div className="lg:w-1/2 border-[2px] border-black bg-white flex flex-col overflow-hidden">
              <div className="bg-[#e6e6e6] border-b-[2px] border-black p-2 text-center font-bold text-xs sm:text-sm flex-shrink-0">
                Box Item Master
              </div>
              <div className="flex-1 overflow-y-auto">
                <table className="w-full border-collapse text-[10px] sm:text-xs">
                  <thead className="bg-[#e6e6e6] sticky top-0 z-10">
                    <tr>
                      <th className="border border-black py-1 px-2">Opening Stock</th>
                      <th className="border border-black py-1 px-2">Code</th>
                      <th className="border border-black py-1 px-2">Box Name Eng</th>
                      <th className="border border-black py-1 px-2">Box Name Guj</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => handleRowClick(row)}
                        className={`cursor-pointer hover:bg-[#f0f0f0] ${selectedRow?.id === row.id ? "bg-yellow-100" : ""}`}
                      >
                        <td className="border border-[#c8c8c8] text-center px-2 py-1">{row.opening_stock}</td>
                        <td className="border border-[#c8c8c8] text-center px-2 py-1">{row.box_code}</td>
                        <td className="border border-[#c8c8c8] px-2 py-1">{row.box_name_eng}</td>
                        <td className="border border-[#c8c8c8] px-2 py-1">{row.box_name_guj}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-[#cfefe0] border-t-[2px] border-black p-2 sm:p-3 flex flex-wrap gap-2 justify-center flex-shrink-0">
            <button
              onClick={handleSave}
              disabled={isSaveDisabled}
              className={`min-w-[80px] sm:min-w-[90px] px-3 py-1 font-bold text-sm border-[2px] border-[#5b4a3d] 
                ${isSaveDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-green-400 hover:bg-green-500"}`}
            >
              Save
            </button>
            <button
              onClick={handleEdit}
              disabled={!isEditEnabled}
              className={`min-w-[80px] px-3 py-1 font-bold text-sm border-[2px] border-[#5b4a3d]
                ${!isEditEnabled ? "bg-gray-400 cursor-not-allowed" : "bg-blue-400 hover:bg-blue-500"}`}
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              disabled={!isDeleteEnabled}
              className={`min-w-[80px] px-3 py-1 font-bold text-sm border-[2px] border-[#5b4a3d]
                ${!isDeleteEnabled ? "bg-gray-400 cursor-not-allowed" : "bg-red-400 hover:bg-red-500"}`}
            >
              Delete
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="min-w-[80px] px-3 py-1 font-bold text-sm border-[2px] border-[#5b4a3d] bg-gray-300 hover:bg-gray-400"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxItemMaster;
